import React, { useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import VisualPageBuilder from '@/components/VisualPageBuilder';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_HOME_HTML = `<div style="font-family: system-ui, -apple-system, sans-serif; color: white; background: #000;">
  <div style="background: rgba(255,255,255,0.05); border-top: 1px solid rgba(255,255,255,0.1); border-bottom: 1px solid rgba(255,255,255,0.1); padding: 5rem 2rem; text-align: center;">
    <h2 style="font-size: 3.5rem; font-weight: 300; margin-bottom: 1.5rem; letter-spacing: 0.2em; color: white;">Καλώς ήρθες στο DIMAKAS</h2>
    <p style="font-size: 1.2rem; color: #9ca3af; margin-bottom: 2.5rem; font-weight: 300; letter-spacing: 0.1em;">Προσφέρουμε επαγγελματική περιποίηση ανδρών</p>
    <button style="background: white; color: black; border: none; padding: 1rem 2.5rem; font-size: 1rem; font-weight: 300; letter-spacing: 0.15em; cursor: pointer; text-transform: uppercase;">Κλείσε Ραντεβού</button>
  </div>
  <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 2rem; padding: 2rem;">
    <div style="background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); padding: 2rem;">
      <h3 style="font-size: 1.8rem; font-weight: 300; margin-bottom: 1rem; letter-spacing: 0.1em; color: white;">Κούρεμα</h3>
      <p style="color: #9ca3af; font-weight: 300; line-height: 1.7;">Προσφέρουμε σύγχρονα κουρέματα προσαρμοσμένα στο στυλ σου.</p>
    </div>
    <div style="background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); padding: 2rem;">
      <h3 style="font-size: 1.8rem; font-weight: 300; margin-bottom: 1rem; letter-spacing: 0.1em; color: white;">Περιποίηση Γενιών</h3>
      <p style="color: #9ca3af; font-weight: 300; line-height: 1.7;">Εξειδικευμένη περιποίηση και τρίμμισμα γενιών.</p>
    </div>
  </div>
  <div style="background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); margin: 0 2rem 2rem; padding: 2rem;">
    <h3 style="font-size: 1.8rem; font-weight: 300; margin-bottom: 1.5rem; letter-spacing: 0.1em; color: white;">Ώρες Λειτουργίας</h3>
    <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 1.5rem; color: #9ca3af; font-weight: 300;">
      <div>
        <p style="font-weight: 400; color: white; margin-bottom: 0.25rem;">Δευτέρα - Παρασκευή:</p>
        <p style="font-size: 1.1rem;">09:00 - 20:00</p>
      </div>
      <div>
        <p style="font-weight: 400; color: white; margin-bottom: 0.25rem;">Σάββατο:</p>
        <p style="font-size: 1.1rem;">09:00 - 18:00</p>
      </div>
    </div>
  </div>
</div>`;

function HomePage({ isAdmin, onNavigateToAppointments }) {
  const [savedContent, setSavedContent] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/pages/home`);
      if (response.data.html) {
        setSavedContent(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch page content:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const contentRef = useRef(null);

  const handleBuilderSaved = () => { fetchContent(); };

  // After saved content renders, style + wire up the "Κλείσε Ραντεβού" button
  useEffect(() => {
    if (!contentRef.current || !savedContent?.html) return;

    // Style the button with 3D effect
    const allBtns = contentRef.current.querySelectorAll('button, a, [role="button"]');
    allBtns.forEach(btn => {
      if (btn.textContent.trim().includes('Ραντεβού')) {
        btn.style.cssText = [
          'display:inline-block',
          'background:linear-gradient(145deg, #ffffff, #e6e6e6)',
          'color:#000',
          'border:none',
          'padding:1rem 2.5rem',
          'font-size:0.9rem',
          'font-weight:500',
          'letter-spacing:0.15em',
          'text-transform:uppercase',
          'cursor:pointer',
          'transition:all 0.3s ease',
          'box-shadow:0 8px 15px rgba(0,0,0,0.3), 0 4px 6px rgba(0,0,0,0.2)',
          'border-radius:4px',
          'position:relative',
          'transform:translateY(0)',
        ].join(';');
        btn.setAttribute('data-nav', 'appointments');
        
        // Add hover effect
        btn.addEventListener('mouseenter', () => {
          btn.style.transform = 'translateY(-2px)';
          btn.style.boxShadow = '0 12px 20px rgba(0,0,0,0.4), 0 6px 10px rgba(0,0,0,0.3)';
          btn.style.background = 'linear-gradient(145deg, #ffffff, #f5f5f5)';
        });
        
        btn.addEventListener('mouseleave', () => {
          btn.style.transform = 'translateY(0)';
          btn.style.boxShadow = '0 8px 15px rgba(0,0,0,0.3), 0 4px 6px rgba(0,0,0,0.2)';
          btn.style.background = 'linear-gradient(145deg, #ffffff, #e6e6e6)';
        });
        
        // Add active (click) effect
        btn.addEventListener('mousedown', () => {
          btn.style.transform = 'translateY(1px)';
          btn.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.2)';
        });
        
        btn.addEventListener('mouseup', () => {
          btn.style.transform = 'translateY(-2px)';
          btn.style.boxShadow = '0 12px 20px rgba(0,0,0,0.4), 0 6px 10px rgba(0,0,0,0.3)';
        });
      }
    });

    // Use event delegation for reliable click handling (works on mobile too)
    const handleClick = (e) => {
      const target = e.target.closest('[data-nav="appointments"]');
      if (!target) {
        // Also check if clicked text contains Ραντεβού
        if (e.target.textContent?.trim().includes('Ραντεβού') &&
            (e.target.tagName === 'BUTTON' || e.target.tagName === 'A')) {
          e.preventDefault();
          onNavigateToAppointments?.();
        }
        return;
      }
      e.preventDefault();
      onNavigateToAppointments?.();
    };

    const container = contentRef.current;
    container.addEventListener('click', handleClick);
    return () => container.removeEventListener('click', handleClick);
  }, [savedContent, onNavigateToAppointments]);

  if (loading) {
    return <div className="text-center text-gray-400 py-12">Φόρτωση...</div>;
  }

  // Scope GrapesJS CSS and add mobile responsive overrides
  const scopedCss = savedContent?.css
    ? savedContent.css
        .replace(/\*\s*\{[^}]*\}/g, '')        // remove all * { ... }
        .replace(/body\s*\{[^}]*\}/g, '')       // remove all body { ... }
        .replace(/(^|})\s*#/g, '$1 .home-gjs-scope #')  // scope ID selectors only
    : '';

  const mobileCss = `
    @media (max-width: 640px) {
      .home-gjs-scope,
      .home-gjs-scope * {
        grid-template-columns: 1fr !important;
        max-width: 100% !important;
        overflow-wrap: break-word !important;
        word-break: break-word !important;
      }
      .home-gjs-scope > div {
        padding: 1rem !important;
      }
      .home-gjs-scope h2 {
        font-size: 1.8rem !important;
        letter-spacing: 0.05em !important;
      }
      .home-gjs-scope h3 {
        font-size: 1.3rem !important;
      }
      .home-gjs-scope p {
        font-size: 1rem !important;
        margin-bottom: 0.75rem !important;
      }
      .home-gjs-scope .p-12 {
        padding: 1.5rem !important;
      }
      .home-gjs-scope .text-5xl {
        font-size: 1.8rem !important;
      }
      .home-gjs-scope .mb-4 {
        margin-bottom: 0.5rem !important;
      }
      .home-gjs-scope .mb-8 {
        margin-bottom: 0.75rem !important;
      }
      .home-gjs-scope .text-3xl {
        font-size: 1.3rem !important;
      }
      .home-gjs-scope .grid-cols-2,
      .home-gjs-scope .md\\:grid-cols-2 {
        grid-template-columns: 1fr !important;
      }
      .home-gjs-scope button {
        padding: 0.5rem 1.2rem !important;
        font-size: 0.75rem !important;
      }
    }
  `;

  const cleanHtml = savedContent?.html
    ? savedContent.html.replace(/<\/?body[^>]*>/gi, '')
    : '';

  return (
    <div data-testid="home-page">
      {savedContent?.html ? (
        <div ref={contentRef} className="home-gjs-scope w-full max-w-full overflow-hidden">
          {scopedCss && (
            <style dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(scopedCss + mobileCss) }} />
          )}
          <div
            className="w-full max-w-full"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cleanHtml) }}
          />
        </div>
      ) : (
        <div className="space-y-8">
          <div className="bg-white/5 backdrop-blur-sm rounded-none p-16 text-center border-t border-b border-white/10">
            <h2 className="text-6xl font-light mb-6 tracking-wide text-white">
              Καλώς ήρθες στο DIMAKAS
            </h2>
            <p className="text-xl text-gray-400 mb-10 font-light tracking-wide">
              Προσφέρουμε επαγγελματική περιποίηση ανδρών
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={onNavigateToAppointments}
                className="text-black px-10 py-4 font-medium text-base tracking-widest shadow-lg hover:shadow-2xl transform hover:-translate-y-1 active:translate-y-0.5 active:shadow-md transition-all duration-300 uppercase"
                style={{
                  background: 'linear-gradient(145deg, #ffffff, #e6e6e6)',
                  boxShadow: '0 8px 15px rgba(0,0,0,0.3), 0 4px 6px rgba(0,0,0,0.2)',
                  borderRadius: '4px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(145deg, #ffffff, #f5f5f5)';
                  e.currentTarget.style.boxShadow = '0 12px 20px rgba(0,0,0,0.4), 0 6px 10px rgba(0,0,0,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(145deg, #ffffff, #e6e6e6)';
                  e.currentTarget.style.boxShadow = '0 8px 15px rgba(0,0,0,0.3), 0 4px 6px rgba(0,0,0,0.2)';
                }}
                data-testid="book-appointment-btn"
              >
                Κλείσε Ραντεβού
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white/5 backdrop-blur-sm rounded-none p-8 border border-white/10 hover:bg-white/10 transition-all duration-300">
              <h3 className="text-3xl font-light mb-4 tracking-wide">Κούρεμα</h3>
              <p className="text-gray-400 font-light leading-relaxed">
                Προσφέρουμε σύγχρονα κουρέματα προσαρμοσμένα στο στυλ σου.
              </p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-none p-8 border border-white/10 hover:bg-white/10 transition-all duration-300">
              <h3 className="text-3xl font-light mb-4 tracking-wide">Περιποίηση Γενιών</h3>
              <p className="text-gray-400 font-light leading-relaxed">
                Εξειδικευμένη περιποίηση και τρίμμισμα γενιών.
              </p>
            </div>
          </div>

          <div className="bg-white/5 backdrop-blur-sm rounded-none p-8 border border-white/10">
            <h3 className="text-3xl font-light mb-6 tracking-wide">Ώρες Λειτουργίας</h3>
            <div className="grid md:grid-cols-2 gap-6 text-gray-400 font-light">
              <div>
                <p className="font-normal text-white mb-1">Δευτέρα - Παρασκευή:</p>
                <p className="text-lg">09:00 - 20:00</p>
              </div>
              <div>
                <p className="font-normal text-white mb-1">Σάββατο:</p>
                <p className="text-lg">09:00 - 18:00</p>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Visual Builder — bottom, admin only */}
      <VisualPageBuilder pageId="home" isAdmin={isAdmin} onSaved={handleBuilderSaved} defaultContent={DEFAULT_HOME_HTML} />
    </div>
  );
}

export default HomePage;
